import React, { useEffect, useState } from 'react';
import { Spinner } from 'react-bootstrap';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import './blogs.css';
import { fetchBlogPosts } from '../../api/blog';

const BLOG_PAGE_SIZE = 9;

function BlogLoadingSpinner({ label }) {
  return (
    <div className="blog-loading" role="status" aria-label={label}>
      <Spinner animation="border" variant="light" />
    </div>
  );
}

function BlogEmptyState({ iconClass, title, text, variant = 'default' }) {
  return (
    <section className={`blog-empty-state blog-empty-state--${variant}`}>
      <div className="blog-empty-card">
        <i className={`blog-empty-icon ${iconClass}`} aria-hidden="true" />
        <h2 className="blog-empty-title">{title}</h2>
        <p className="blog-empty-text">{text}</p>
      </div>
    </section>
  );
}

export default function BlogIndex() {
  const [posts, setPosts] = useState([]);
  const [pageIndex, setPageIndex] = useState(0);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [status, setStatus] = useState('loading');

  useEffect(() => {
    let active = true;
    setStatus('loading');
    setPosts([]);
    setHasNextPage(false);
    fetchBlogPosts({
      limit: BLOG_PAGE_SIZE + 1,
      offset: pageIndex * BLOG_PAGE_SIZE,
    })
      .then((items) => {
        if (!active) return;
        setPosts(items.slice(0, BLOG_PAGE_SIZE));
        setHasNextPage(items.length > BLOG_PAGE_SIZE);
        setStatus('ready');
      })
      .catch(() => {
        if (!active) return;
        setPosts([]);
        setHasNextPage(false);
        setStatus('error');
      });
    return () => {
      active = false;
    };
  }, [pageIndex]);

  const showPagination = status === 'ready' && (pageIndex > 0 || hasNextPage);
  const canonicalUrl = 'https://botspot.trade/blog';
  const seoTitle = 'BotSpot Blog';
  const seoDescription = 'BotSpot newsletters, market automation tutorials, and trading bot product updates.';
  const seoImage = 'https://botspot.trade/social-share-image.jpg';

  return (
    <main className="blog-index-page">
      <Helmet>
        <title>{seoTitle}</title>
        <meta name="description" content={seoDescription} />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:title" content={seoTitle} />
        <meta property="og:description" content={seoDescription} />
        <meta property="og:image" content={seoImage} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={seoTitle} />
        <meta name="twitter:description" content={seoDescription} />
        <meta name="twitter:image" content={seoImage} />
      </Helmet>

      <section className="blog-index-header">
        <h1>Latest BotSpot articles</h1>
      </section>

      {status === 'loading' && <BlogLoadingSpinner label="Loading articles" />}
      {status === 'error' && (
        <BlogEmptyState
          iconClass="fas fa-exclamation-circle"
          title="Articles are unavailable right now"
          text="Please check back shortly."
          variant="error"
        />
      )}
      {status === 'ready' && posts.length === 0 && (
        <BlogEmptyState
          iconClass="fas fa-newspaper"
          title={pageIndex === 0 ? 'No articles published yet' : 'No older articles found'}
          text={pageIndex === 0
            ? 'Articles will appear here as soon as they are published.'
            : 'Go back to a newer page to continue browsing published articles.'}
        />
      )}

      <section className="blog-index-list" aria-label="Blog articles">
        {posts.map((post) => (
          <article className="blog-index-item" key={post.slug}>
            {post.heroImageUrl && (
              <img
                src={post.heroImageUrl}
                alt={post.heroImageAlt || `${post.title} article cover`}
                className="blog-index-image"
                loading="lazy"
              />
            )}
            <div className="blog-index-copy">
              <time className="blog-index-date" dateTime={post.publishedAt || undefined}>
                {post.publishedAt
                  ? new Date(post.publishedAt).toLocaleDateString('en-US', {
                      month: 'long',
                      day: 'numeric',
                      year: 'numeric',
                    })
                  : post.issueDate}
              </time>
              <h2>
                <Link to={`/blog/${post.slug}`}>{post.title}</Link>
              </h2>
              {post.description && <p>{post.description}</p>}
            </div>
          </article>
        ))}
      </section>

      {showPagination && (
        <nav className="blog-pagination" aria-label="Blog pagination">
          <button
            type="button"
            className="blog-pagination-button"
            disabled={pageIndex === 0}
            onClick={() => setPageIndex((current) => Math.max(current - 1, 0))}
          >
            <i className="fas fa-chevron-left" aria-hidden="true" />
            Newer
          </button>
          <span className="blog-pagination-page">Page {pageIndex + 1}</span>
          <button
            type="button"
            className="blog-pagination-button"
            disabled={!hasNextPage}
            onClick={() => setPageIndex((current) => current + 1)}
          >
            Older
            <i className="fas fa-chevron-right" aria-hidden="true" />
          </button>
        </nav>
      )}
    </main>
  );
}
